import { apiCall } from "api/axios";
import { apiErrorHandler } from "api/axios/error-handler";
import { getTodayAsEndDateFilterValue } from "utils";

import {
  IExportFlexSavingsPayload,
  IExportGoalSavingsPayload,
  IExportResponseData,
  IExportVaultSavingsPayload,
  IGetIndividualVaultSavingsDetailsPayload,
  IGetIndividualVaultSavingsPayload,
  IGetIndividualVaultSavingsTransactionsPayload,
  IGetIndividualVaultSavingsTransactionsStatsPayload,
  IIndividualFlexSavingsDetailsPayload,
  IIndividualFlexSavingsDetailsResponseData,
  IIndividualFlexSavingsHistoryPayload,
  IIndividualFlexSavingsHistoryResponseData,
  IIndividualFlexSavingsPayload,
  IIndividualFlexSavingsResponseData,
  IIndividualFlexSavingsStatsPayload,
  IIndividualFlexSavingsStatsResponseDatas,
  IIndividualGoalSavingsDetailsPayload,
  IIndividualGoalSavingsDetailsResponseData,
  IIndividualGoalSavingsHistoryPayload,
  IIndividualGoalSavingsHistoryResponseData,
  IIndividualGoalSavingsPayload,
  IIndividualGoalSavingsResponseData,
  IIndividualGoalSavingsStatsPayload,
  IIndividualGoalSavingsStatsResponseDatas,
  IIndividualSavingsCountResponseData,
  IIndividualSavingsTransactionsStatsPayload,
  IIndividualSavingsTransactionsStatsResponseData,
  IIndividualVaultSavingsDetailsResponseData,
  IIndividualVaultSavingsResponseData,
  IIndividualVaultSavingsStatsPayload,
  IIndividualVaultSavingsStatsResponseDatas,
  IIndividualVaultSavingsTransactionResponseData,
  IIndividualVaultSavingsTransactionsStatsResponseData,
  IPauseIndividualSavingsPayload,
  IPauseIndividualSavingsResponseData,
  IResumeIndividualSavingsPayload,
  IResumeIndividualSavingsResponseData,
} from "./types";

export const getIndividualFlexSavingsStats = async (
  payload: IIndividualFlexSavingsStatsPayload
) => {
  const payloadData = { ...payload };
  if (payloadData.startDate && !payloadData.endDate) {
    payloadData.endDate = getTodayAsEndDateFilterValue();
  }
  const queryString = Object.entries(payloadData)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");

  const result = await apiCall({
    apiRoute: `/control/savings/flex/stats/details${queryString ? `?${queryString}` : ""}`,
    module: "EARNIPAY_API",
    config: {
      method: "GET",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IIndividualFlexSavingsStatsResponseDatas;
};

export const getIndividualFlexSavings = async (
  payload: IIndividualFlexSavingsPayload
) => {
  const payloadData = { ...payload };
  if (payloadData.startDate && !payloadData.endDate) {
    payloadData.endDate = getTodayAsEndDateFilterValue();
  }
  const queryString = Object.entries(payloadData)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");

  const result = await apiCall({
    apiRoute: `/control/savings/flex${queryString ? `?${queryString}` : ""}`,
    module: "EARNIPAY_API",
    config: {
      method: "GET",
    },
    useEncryption: true,
  });
  return result as IIndividualFlexSavingsResponseData;
};

export const getIndividualFlexSavingsDetails = async (
  payload: IIndividualFlexSavingsDetailsPayload
) => {
  const { id } = payload;
  const result = await apiCall({
    apiRoute: `/control/savings/flex/details/${id}`,
    module: "EARNIPAY_API",
    config: {
      method: "GET",
    },
    useEncryption: true,
  });
  return result as IIndividualFlexSavingsDetailsResponseData;
};

export const getIndividualFlexSavingsHistory = async (
  payload: IIndividualFlexSavingsHistoryPayload
) => {
  const payloadData = { ...payload };
  if (payloadData.startDate && !payloadData.endDate) {
    payloadData.endDate = getTodayAsEndDateFilterValue();
  }
  const queryString = Object.entries(payloadData)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");
  const result = await apiCall({
    apiRoute: `/control/savings/flex/transactions/details${queryString ? `?${queryString}` : ""}`,
    module: "EARNIPAY_API",
    config: {
      method: "GET",
    },
    useEncryption: true,
  });
  return result as IIndividualFlexSavingsHistoryResponseData;
};

export const pauseIndividualFlexSavings = async (
  payload: IPauseIndividualSavingsPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/personal/user-management/savings/pause",
    module: "EARNIPAY_API",
    config: {
      method: "PATCH",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IPauseIndividualSavingsResponseData;
};

export const resumeIndividualFlexSavings = async (
  payload: IResumeIndividualSavingsPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/personal/user-management/savings/resume",
    module: "EARNIPAY_API",
    config: {
      method: "PATCH",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IResumeIndividualSavingsResponseData;
};

export const exportFlexSavings = async (payload: IExportFlexSavingsPayload) => {
  const data = { ...payload };
  if (data.startDate && !data.endDate) {
    data["endDate"] = getTodayAsEndDateFilterValue();
  }
  const result = await apiCall({
    apiRoute: "/control/personal/user-management/savings/export",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data,
    },
    useEncryption: true,
  });
  return result as IExportResponseData;
};

export const exportGoalSavings = async (payload: IExportGoalSavingsPayload) => {
  const data = { ...payload };
  if (data.startDate && !data.endDate) {
    data["endDate"] = getTodayAsEndDateFilterValue();
  }
  const result = await apiCall({
    apiRoute: "/control/personal/user-management/savings/export",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data,
    },
    useEncryption: true,
  });
  return result as IExportResponseData;
};

export const exportVaultSavings = async (
  payload: IExportVaultSavingsPayload
) => {
  const data = { ...payload };
  if (data.startDate && !data.endDate) {
    data["endDate"] = getTodayAsEndDateFilterValue();
  }
  const result = await apiCall({
    apiRoute: "/control/personal/user-management/savings/export",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data,
    },
    useEncryption: true,
  });
  return result as IExportResponseData;
};

// export const exportFlexSavingsHistory = async (
//   payload: IExportSavingsHistoryPayload
// ) => {
//   const data = { ...payload };
//   if (data.startDate && !data.endDate) {
//     data["endDate"] = getTodayAsEndDateFilterValue();
//   }
//   const result = await apiCall({
//     apiRoute: "/control/personal/user-management/savings/transactions-export",
//     module: "EARNIPAY_API",
//     config: {
//       method: "POST",
//       data,
//     },
//     useEncryption: true,
//   });
//   return result as IExportResponseData;
// };

export const getIndividualGoalSavingsStats = async (
  payload: IIndividualGoalSavingsStatsPayload
) => {
  const payloadData = { ...payload };
  if (payloadData.startDate && !payloadData.endDate) {
    payloadData.endDate = getTodayAsEndDateFilterValue();
  }

  const queryString = Object.entries(payloadData)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");
  const result = await apiCall({
    apiRoute: `/savings/goal/admin/list/stat${queryString ? `?${queryString}` : ""}`,
    module: "EARNIPAY_API",
    config: {
      method: "GET",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IIndividualGoalSavingsStatsResponseDatas;
};

export const getIndividualGoalSavings = async (
  payload: IIndividualGoalSavingsPayload
) => {
  const payloadData = { ...payload };
  if (payloadData.startDate && !payloadData.endDate) {
    payloadData.endDate = getTodayAsEndDateFilterValue();
  }
  const queryString = Object.entries(payloadData)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");

  const result = await apiCall({
    apiRoute: `/savings/goal/admin/list${queryString ? `?${queryString}` : ""}&type=target`,
    module: "EARNIPAY_API",
    config: {
      method: "GET",
    },
    useEncryption: true,
  });
  return result as IIndividualGoalSavingsResponseData;
};

export const getIndividualGoalSavingsDetails = async (
  payload: IIndividualGoalSavingsDetailsPayload
) => {
  const result = await apiCall({
    apiRoute: "/savings/goal/admin/list/detail",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IIndividualGoalSavingsDetailsResponseData;
};

export const getIndividualGoalSavingsHistory = async (
  payload: IIndividualGoalSavingsHistoryPayload
) => {
  if (payload.startDate && !payload.endDate) {
    payload.endDate = getTodayAsEndDateFilterValue(); //to update the end date to same day
  }
  const result = await apiCall({
    apiRoute: "/savings/goal/admin/list/transaction",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IIndividualGoalSavingsHistoryResponseData;
};

export const getIndividualVaultSavingsStats = async (
  payload: IIndividualVaultSavingsStatsPayload
) => {
  if (payload.startDate && !payload.endDate) {
    payload.endDate = getTodayAsEndDateFilterValue();
  }
  const queryString = Object.entries(payload)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");

  const result = (await apiCall({
    apiRoute: `/control/savings/personal-vault/stats${queryString && `?${queryString}`}`,
    module: "EARNIPAY_API",
    config: {
      method: "GET",
    },
    useEncryption: true,
  })) as IIndividualVaultSavingsStatsResponseDatas;
  apiErrorHandler({
    result,
    defaultErrorMsg: "Error getting vault savings stats",
  });
  return result;
};

export const getIndividualVaultSavings = async (
  payload: IGetIndividualVaultSavingsPayload
) => {
  if (payload.startDate && !payload.endDate) {
    payload.endDate = getTodayAsEndDateFilterValue();
  }
  const queryString = Object.entries(payload)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");

  const result = (await apiCall({
    apiRoute: `/control/savings/personal-vault?${queryString}`,
    module: "EARNIPAY_API",
    config: {
      method: "GET",
    },
    useEncryption: true,
  })) as IIndividualVaultSavingsResponseData;
  apiErrorHandler({
    result,
    defaultErrorMsg: "Error fetching vault savings",
  });
  return result;
};

// Flex, Goal Savings Transactions Stats
export const getIndividualSavingsTransactionsStats = async (
  payload: IIndividualSavingsTransactionsStatsPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/personal/user-management/savings/transactions/stat",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IIndividualSavingsTransactionsStatsResponseData;
};

export const getIndividualVaultSavingsDetails = async (
  payload: IGetIndividualVaultSavingsDetailsPayload
) => {
  const result = (await apiCall({
    apiRoute: "/control/savings/personal-vault/detail",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: payload,
    },
    useEncryption: true,
  })) as IIndividualVaultSavingsDetailsResponseData;
  apiErrorHandler({
    result,
    defaultErrorMsg: "Error fetching vault savings details",
  });
  return result;
};

export const getIndividualVaultSavingsTransactions = async (
  payload: IGetIndividualVaultSavingsTransactionsPayload
) => {
  const result = (await apiCall({
    apiRoute: "/control/savings/personal-vault/transactions",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: payload,
    },
    useEncryption: true,
  })) as IIndividualVaultSavingsTransactionResponseData;
  apiErrorHandler({
    result,
    defaultErrorMsg: "Error fetching vault savings transactions",
  });
  return result;
};

export const getIndividualVaultSavingsTransactionsStats = async (
  payload: IGetIndividualVaultSavingsTransactionsStatsPayload
) => {
  const result = (await apiCall({
    apiRoute: "/control/savings/personal-vault/transactions/stat",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: payload,
    },
    useEncryption: true,
  })) as IIndividualVaultSavingsTransactionsStatsResponseData;

  apiErrorHandler({
    result,
    defaultErrorMsg: "Error fetching vault savings transactions stats",
  });
  return result;
};

export const getIndividualSavingsCount = async () => {
  const result = (await apiCall({
    apiRoute: "/control/personal/user-management/savings/counts",
    module: "EARNIPAY_API",
    config: {
      method: "GET",
    },
    useEncryption: true,
  })) as IIndividualSavingsCountResponseData;

  apiErrorHandler({
    result,
    defaultErrorMsg: "Error getting individual savings count",
  });
  return result;
};
